/**
 * Layout debug timeline for diagnosing chat scroll jumps, bubble reflow, and cursor drift.
 */

import {
  extractStreamingMessage,
  isMessageStreaming,
  type ParlantEvent,
} from '../types/parlant';

export interface LayoutRect {
  x: number;
  y: number;
  width: number;
  height: number;
  bottom: number;
  right: number;
}

export interface LayoutDebugScrollState {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  distanceFromBottom: number;
}

export interface LayoutDebugBubbleState {
  id: string;
  offset: string;
  rect: LayoutRect;
  contentRect: LayoutRect | null;
  contentHeight: number;
  hasCursor: boolean;
}

export interface LayoutDebugSampleDelta {
  scrollTop: number;
  scrollHeight: number;
  activeBubbleY: number;
  activeContentHeight: number;
  cursorY: number;
}

export interface LayoutDebugSample {
  t: number;
  scroll: LayoutDebugScrollState | null;
  content: LayoutRect | null;
  bubbles: LayoutDebugBubbleState[];
  cursor: LayoutRect | null;
  activeBubbleId: string | null;
  delta: LayoutDebugSampleDelta | null;
}

export interface LayoutDebugEvent {
  t: number;
  type: string;
  data: Record<string, unknown>;
}

export interface LayoutDebugServerEvent {
  t: number;
  source: string;
  event: Record<string, unknown>;
}

export interface LayoutDebugTimeline {
  startedAt: number;
  exportedAt: number;
  sampleCount: number;
  eventCount: number;
  serverEventCount: number;
  samples: LayoutDebugSample[];
  events: LayoutDebugEvent[];
  serverEvents: LayoutDebugServerEvent[];
}

const MAX_SAMPLES = 9000;
const MAX_EVENTS = 2000;
const MAX_SERVER_EVENTS = 8000;
const HEARTBEAT_MS = 500;
const POSITION_EPSILON = 0.5;

let enabled = false;

const samples: LayoutDebugSample[] = [];
const events: LayoutDebugEvent[] = [];
const serverEvents: LayoutDebugServerEvent[] = [];
let startedAt = 0;
let lastHeartbeatAt = 0;
let lastSample: LayoutDebugSample | null = null;

export function setLayoutDebugEnabled(value: boolean): void {
  enabled = value;
  if (value && startedAt === 0) {
    startedAt = performance.now();
  }
  if (!value) {
    clearLayoutDebugTimeline();
  }
}

export function isLayoutDebugEnabled(): boolean {
  return enabled;
}

export function clearLayoutDebugTimeline(): void {
  samples.length = 0;
  events.length = 0;
  serverEvents.length = 0;
  startedAt = enabled ? performance.now() : 0;
  lastHeartbeatAt = 0;
  lastSample = null;
}

/**
 * Summarizes a Parlant event for debug export (avoids dumping full chunk arrays).
 */
export function summarizeParlantEventForDebug(event: ParlantEvent): Record<string, unknown> {
  const data = (event.data ?? {}) as Record<string, unknown>;
  const chunks = data.chunks as (string | null)[] | undefined;
  const assembled = extractStreamingMessage(data);
  const messageText =
    typeof assembled === 'string'
      ? assembled
      : typeof data.message === 'string'
        ? data.message
        : undefined;

  return {
    id: event.id,
    offset: event.offset,
    kind: event.kind,
    source: event.source,
    deleted: event.deleted === true,
    correlationId: event.correlation_id,
    creationUtc: event.creation_utc,
    status: event.kind === 'status' ? data.status : undefined,
    stage:
      event.kind === 'status'
        ? (data.data as Record<string, unknown> | undefined)?.stage
        : undefined,
    messagePreview: messageText?.slice(0, 200),
    messageLength: messageText?.length ?? 0,
    streaming: event.kind === 'message' ? isMessageStreaming(data) : false,
    chunkCount: Array.isArray(chunks) ? chunks.length : undefined,
    streamTerminated:
      Array.isArray(chunks) && chunks.length > 0 && chunks[chunks.length - 1] === null,
    interrupted: data._interrupted === true,
    turnKey:
      event.kind === 'message' &&
      (event.source === 'ai_agent' || event.source === 'human_agent') &&
      event.correlation_id
        ? `turn-${event.correlation_id}-${event.offset}`
        : undefined,
  };
}

export function noteServerEvents(
  source: string,
  incoming: ParlantEvent[],
  meta: Record<string, unknown> = {}
): void {
  if (!enabled || incoming.length === 0) return;

  const t = performance.now();

  for (const event of incoming) {
    const summary = summarizeParlantEventForDebug(event);
    serverEvents.push({
      t,
      source,
      event: { ...summary, ...meta },
    });
  }

  if (serverEvents.length > MAX_SERVER_EVENTS) {
    serverEvents.splice(0, serverEvents.length - MAX_SERVER_EVENTS);
  }

  noteLayoutDebugEvent('server-events', {
    source,
    count: incoming.length,
    offsets: incoming.map((event) => event.offset),
    kinds: incoming.map((event) => event.kind),
    ...meta,
  });

  if (typeof console !== 'undefined') {
    console.debug('[layout-debug] server-events', {
      source,
      count: incoming.length,
      events: incoming.map(summarizeParlantEventForDebug),
      ...meta,
    });
  }
}

export function noteLayoutDebugEvent(type: string, data: Record<string, unknown> = {}): void {
  if (!enabled) return;

  events.push({
    t: performance.now(),
    type,
    data,
  });

  if (events.length > MAX_EVENTS) {
    events.splice(0, events.length - MAX_EVENTS);
  }
}

export function serializeRect(rect: DOMRectReadOnly | DOMRect | null | undefined): LayoutRect | null {
  if (!rect) return null;

  return {
    x: Math.round(rect.x * 10) / 10,
    y: Math.round(rect.y * 10) / 10,
    width: Math.round(rect.width * 10) / 10,
    height: Math.round(rect.height * 10) / 10,
    bottom: Math.round(rect.bottom * 10) / 10,
    right: Math.round(rect.right * 10) / 10,
  };
}

function numDelta(current: number, previous: number): number {
  return Math.round((current - previous) * 10) / 10;
}

export function computeSampleDelta(
  current: LayoutDebugSample,
  previous: LayoutDebugSample
): LayoutDebugSampleDelta | null {
  const activeBubble = current.bubbles.find((bubble) => bubble.id === current.activeBubbleId);
  const prevActiveBubble = previous.bubbles.find((bubble) => bubble.id === previous.activeBubbleId);

  const delta: LayoutDebugSampleDelta = {
    scrollTop: numDelta(current.scroll?.scrollTop ?? 0, previous.scroll?.scrollTop ?? 0),
    scrollHeight: numDelta(current.scroll?.scrollHeight ?? 0, previous.scroll?.scrollHeight ?? 0),
    activeBubbleY: numDelta(activeBubble?.rect.y ?? 0, prevActiveBubble?.rect.y ?? 0),
    activeContentHeight: numDelta(
      activeBubble?.contentHeight ?? 0,
      prevActiveBubble?.contentHeight ?? 0
    ),
    cursorY: numDelta(current.cursor?.y ?? 0, previous.cursor?.y ?? 0),
  };

  const hasMovement =
    Math.abs(delta.scrollTop) > POSITION_EPSILON ||
    Math.abs(delta.scrollHeight) > POSITION_EPSILON ||
    Math.abs(delta.activeBubbleY) > POSITION_EPSILON ||
    Math.abs(delta.activeContentHeight) > POSITION_EPSILON ||
    Math.abs(delta.cursorY) > POSITION_EPSILON;

  return hasMovement ? delta : null;
}

export function shouldRecordLayoutSample(
  current: LayoutDebugSample,
  previous: LayoutDebugSample | null,
  now: number
): boolean {
  if (!previous) return true;
  if (now - lastHeartbeatAt >= HEARTBEAT_MS) return true;

  const delta = computeSampleDelta(current, previous);
  return delta !== null;
}

export function recordLayoutDebugSample(sample: LayoutDebugSample): void {
  if (!enabled) return;

  if (startedAt === 0) {
    startedAt = sample.t;
  }

  const enriched: LayoutDebugSample = { ...sample, delta: null };

  if (lastSample) {
    enriched.delta = computeSampleDelta(enriched, lastSample);
  }

  if (!shouldRecordLayoutSample(enriched, lastSample, sample.t)) {
    return;
  }

  lastHeartbeatAt = sample.t;
  lastSample = enriched;
  samples.push(enriched);

  if (samples.length > MAX_SAMPLES) {
    samples.splice(0, samples.length - MAX_SAMPLES);
  }

  if (enriched.delta) {
    const { scrollTop, activeBubbleY, activeContentHeight, cursorY } = enriched.delta;
    const significant =
      Math.abs(scrollTop) > 1 ||
      Math.abs(activeBubbleY) > 1 ||
      Math.abs(activeContentHeight) > 1 ||
      Math.abs(cursorY) > 1;

    if (significant && typeof console !== 'undefined') {
      console.debug('[layout-debug] movement', {
        t: enriched.t,
        activeBubbleId: enriched.activeBubbleId,
        delta: enriched.delta,
      });
    }
  }
}

export function getLayoutDebugTimeline(): LayoutDebugTimeline {
  return {
    startedAt,
    exportedAt: performance.now(),
    sampleCount: samples.length,
    eventCount: events.length,
    serverEventCount: serverEvents.length,
    samples: [...samples],
    events: [...events],
    serverEvents: [...serverEvents],
  };
}

export function downloadLayoutDebugTimeline(filename?: string): void {
  const timeline = getLayoutDebugTimeline();
  const blob = new Blob([JSON.stringify(timeline, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  const stamp = new Date().toISOString().replace(/[:.]/g, '-');
  anchor.href = url;
  anchor.download = filename ?? `chat-layout-debug-${stamp}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}

declare global {
  interface Window {
    __layoutDebug?: {
      download: (filename?: string) => void;
      clear: () => void;
      timeline: () => LayoutDebugTimeline;
      enabled: () => boolean;
    };
  }
}

export function registerLayoutDebugConsoleApi(): void {
  if (typeof window === 'undefined') return;

  window.__layoutDebug = {
    download: downloadLayoutDebugTimeline,
    clear: clearLayoutDebugTimeline,
    timeline: getLayoutDebugTimeline,
    enabled: isLayoutDebugEnabled,
  };
}
